import React, { useState } from "react";
import Table from "react-bootstrap/Table";
import Collapse from "react-bootstrap/Collapse";
import "../../projects/scss/task.scss";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import idx from "idx";
import { subcontractorObjFields } from "../../../constants/fields.constant";
import { updateSubTaskAction } from "../../../slice/add-task";
import { useDispatch } from "react-redux";
import _ from "lodash";
import CustomInput from "../../../components/inputs/new-input";
import SelectInput from "../../../components/inputs/select";

const filter = createFilterOptions();

/**
 * Subcontractor Component
 *
 * A user-friendly, interactive component for managing subcontractor resources in a sub-task.
 * Allows selection from predefined contractor options, cost input, and subtotal calculation with
 * support for inline editing and dynamic row management.
 *
 * @Modified CTBL-275 - Subcontractors list UI fix
 *
 * MAJOR FEATURES:
 * 1. Contractor Management:
 *    - Display of existing subcontractor entries in a table format
 *    - Allows inline editing of contractor name, description, and cost
 *    - Dynamically calculates total and subtotal values
 *    - Delete icon for removing individual entries
 *
 * 2. Add Contractor:
 *    - Expandable form using Collapse component
 *    - Autocomplete dropdown for selecting contractor from predefined list
 *    - Pre-fills default object structure using `subcontractorObjFields`
 *    - Adds contractor to Redux-managed sub-task state
 *
 * 3. State Management:
 *    - Maintains local state for the add form toggle and temporary contractor object
 *    - Dispatches `updateSubTaskAction` to persist changes to Redux state
 *    - Ensures deep immutability with `lodash.cloneDeep`
 *
 * UI/UX IMPROVEMENTS:
 * 1. Table:
 *    - Responsive layout using Bootstrap Table
 *    - Consistent field spacing and alignment
 *    - Read-only mode support via `isDisabled` prop
 *
 * 2. Inputs:
 *    - Uses `CustomInput` and `SelectInput` for controlled, validated inputs
 *    - Decimal formatting for cost fields
 *    - Label-based UI using material-style Autocomplete
 *
 * 3. Interaction:
 *    - Add contractor interaction triggers row expansion
 *    - Smooth collapse animation for add form
 *    - Prevents duplicate or invalid additions
 *
 * TECHNICAL ENHANCEMENTS:
 * 1. Modularity:
 *    - Component operates independently, scoped to a sub-task
 *    - Accepts external data for contractors and configuration via props
 *
 * 2. Redux Integration:
 *    - Synchronizes changes to contractor list via dispatched action
 *    - Handles proper object population using `subcontractorObjFields`
 *
 * 3. Extensibility:
 *    - Structured for easy addition of sale amount or margin fields
 *    - Placeholder logic present for future enhancement of profit lock features
 *
 * @component
 * @param {Object} props
 * @param {Array} props.subcontractor - Array of subcontractor resources for the current sub-task
 * @param {Object} props.total - Aggregated total cost for all contractors
 * @param {number} props.subTaskArrayIndex - Index of the current sub-task within the parent task array
 * @param {number} props.profitMargin - Optional profit margin (not used yet)
 * @param {string} props.profitMarginType - Type of margin (fixed/percentage, not used yet)
 * @param {Array} props.subcontractorOptions - List of available subcontractors to choose from
 * @param {boolean} props.isDisabled - Flag to control view-only mode
 * @param {Array} props.subTaskArray - Full task list from Redux (used for updating)
 * @returns {JSX.Element} Contractor management table with inline edit/add capabilities
 */

export default function Subcontractor({
  subcontractor = [],
  total = {},
  subTaskArrayIndex,
  profitMargin,
  profitMarginType,
  subcontractorOptions = [],
  isDisabled,
  ...props
}) {
  const dispatch = useDispatch();
  const [subcontractorState, setSubcontractorState] = useState({
    subcontractorObj: subcontractorObjFields,
    confirmLock: false,
    lockArrayValue: null,
  });
  const [expandAddSubcontractor, setExpandAddSubcontractor] = useState(false);

  const filterSubcontractor = (id) => {
    return subcontractorOptions.find((_) => _.value === id) || {};
  };
  const updateSubcontractor = (e, index) => {
    const { subTaskArray } = props;
    let _temp = _.cloneDeep(subTaskArray);

    const selectedSubcontractor = filterSubcontractor(e.value);

    const _subcontractor = JSON.parse(
      JSON.stringify(subcontractorState.subcontractorObj)
    );

    _temp[subTaskArrayIndex]["subcontractor"][index]["name"].value =
      selectedSubcontractor.key;
    _temp[subTaskArrayIndex]["subcontractor"][index]["id"].value =
      selectedSubcontractor.value;
    _temp[subTaskArrayIndex]["subcontractor"][index]["Cost"].value =
      _subcontractor.Cost.value;
    _temp[subTaskArrayIndex]["subcontractor"][index]["saleAmount"].value =
      _temp[subTaskArrayIndex]["subcontractor"][index]["Cost"].value;
    _temp[subTaskArrayIndex]["subcontractor"][index]["description"].value =
      _subcontractor.description.value;
    _temp[subTaskArrayIndex]["subcontractor"][index]["total"].value =
      parseInt(_subcontractor.Cost.value) || 0;
    dispatch(updateSubTaskAction(_temp));
  };

  const subcontractorChangeHandler = (e, index) => {
    const { subTaskArray } = props;
    let _temp = _.cloneDeep(subTaskArray);

    const value = e.target.value.replace("-", "");
    if (
      _temp[subTaskArrayIndex]["subcontractor"][index][e.target.name].name ===
      "saleAmount"
    ) {
      _temp[subTaskArrayIndex]["subcontractor"][index]["saleAmount"].value =
        e.target.value;
      _temp[subTaskArrayIndex]["subcontractor"][index][
        "saleAmountLock"
      ].value = false;
    } else if (
      _temp[subTaskArrayIndex]["subcontractor"][index][e.target.name].name ===
      "Cost"
    ) {
      _temp[subTaskArrayIndex]["subcontractor"][index]["Cost"].value =
        e.target.value;

      _temp[subTaskArrayIndex]["subcontractor"][index]["saleAmount"].value =
        _temp[subTaskArrayIndex]["subcontractor"][index]["Cost"].value;
    } else {
      _temp[subTaskArrayIndex]["subcontractor"][index][e.target.name].value =
        value;
    }
    _temp[subTaskArrayIndex]["subcontractor"][index]["total"].value =
      parseFloat(
        _temp[subTaskArrayIndex]["subcontractor"][index]["Cost"].value
      ) || 0;

    dispatch(updateSubTaskAction(_temp));
  };

  const removeSubcontractor = (index) => {
    const { subTaskArray } = props;
    let _temp = _.cloneDeep(subTaskArray);
    const subcontractor = _temp[subTaskArrayIndex]["subcontractor"][index];

    subcontractor.Cost.value = "0";
    _temp[subTaskArrayIndex]["subcontractor"].splice(index, 1);
    dispatch(updateSubTaskAction(_temp));
  };

  const addSubcontractor = (e, itemData = null) => {
    const { subTaskArray } = props;
    let _temp = _.cloneDeep(subTaskArray);

    const subcontractor = JSON.parse(
      JSON.stringify(subcontractorState.subcontractorObj)
    );
    subcontractor.name.value = e.key;
    subcontractor.id.value = e.value;
    _temp[subTaskArrayIndex]["subcontractor"].push(subcontractor);
    dispatch(updateSubTaskAction(_temp));
    setSubcontractorState((prev) => ({
      ...prev,
      subcontractorObj: subcontractorObjFields,
    }));
  };

  return (
    <>
      <h2>Contractor List</h2>
      <div className="table-responsive">
        <Table bordered className="table-create table-material">
          <thead>
            <tr>
              <th className="autocomplete-width header-list-width">
                Contractor(s)
              </th>
              <th>Description</th>
              <th>Cost</th>
              <th className="header-list-total"></th>
              {/* <th>Sale Amount($)</th> */}

              <th className="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {subcontractor.map((_subcontractor, subcontractorIndex) => {
              if (!_subcontractor) return;
              return (
                <tr key={subcontractorIndex}>
                  <td>
                    <SelectInput
                      className="input-block-sm"
                      label={"Select Contractor"}
                      items={subcontractorOptions}
                      value={idx(_subcontractor, (_) => _.id.value) || ""}
                      onChange={(e) =>
                        updateSubcontractor(
                          { value: e.target.value },
                          subcontractorIndex
                        )
                      }
                    />
                  </td>
                  <td className="input-text-right">
                    <CustomInput
                      className="input-block-sm"
                      disabled={isDisabled}
                      placeholder="Description"
                      {..._subcontractor.description}
                      onChange={(e) =>
                        subcontractorChangeHandler(e, subcontractorIndex)
                      }
                    />
                  </td>
                  <td className="input-text-right">
                    <CustomInput
                      className="input-block-sm"
                      disabled={isDisabled}
                      placeholder="Cost"
                      {..._subcontractor.Cost}
                      onChange={(e) =>
                        subcontractorChangeHandler(e, subcontractorIndex)
                      }
                      min={1}
                      formatDecimals={true}
                    />
                  </td>

                  <td className="header-list-total"></td>
                  <td className="text-right">
                    {!isDisabled && (
                      <DeleteIcon
                        className="deleteicon"
                        onClick={() => removeSubcontractor(subcontractorIndex)}
                      />
                    )}
                  </td>
                </tr>
              );
            })}

            <tr>
              <td>
                {!isDisabled && (
                  <span
                    className="add-material"
                    onClick={() =>
                      setExpandAddSubcontractor(!expandAddSubcontractor)
                    }
                    aria-controls="example-collapse-texts"
                    aria-expanded={expandAddSubcontractor}
                  >
                    <AddIcon /> Add Contractor
                  </span>
                )}
              </td>
              <td></td>
              <td className="text-right table-text-sm">Sub Total</td>

              <td className="text-right table-text-sm">
                $
                {parseFloat(
                  idx(total, (_) => _.subcontractor.sale) || 0
                ).toFixed(2)}
              </td>
              <td></td>
            </tr>
          </tbody>
        </Table>
      </div>

      <Collapse in={expandAddSubcontractor}>
        <div id="example-collapse-texts">
          <div className="row m-0">
            <div className="col-lg-5 col-md-6 my-3">
              <div className="form-group">
                <div className="input-block autocomplete-block ">
                  <label className="input-label">Contractor</label>
                  <Autocomplete
                    disablePortal
                    id="combo-box-demo"
                    onChange={(event, newValue) => {
                      if (typeof newValue === "string") {
                        newValue = { inputValue: newValue };
                      } else if (newValue && newValue.inputValue) {
                        //   console.info(newValue.inputValue);
                      } else if (newValue) {
                        addSubcontractor(newValue, null);
                        setExpandAddSubcontractor(false);
                      }
                    }}
                    filterOptions={(options, params) => {
                      return filter(options, params);
                    }}
                    options={subcontractorOptions}
                    getOptionLabel={(option) => {
                      // e.g value selected with enter, right from the input
                      if (typeof option === "string") {
                        return option;
                      }
                      return option.key;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label="Contractor"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Collapse>
    </>
  );
}
